import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { faArrowAltCircleDown } from "@fortawesome/free-solid-svg-icons/faArrowAltCircleDown";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraDropdown } from "../../../../components/AtiraDropdown";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { Clickable } from "../../../../components/Clickable";
import { Drawer } from "../../../../components/Drawer";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Input } from "../../../../components/Input";
import { Text } from "../../../../components/Text";
import { Textarea } from "../../../../components/Textarea";
import { WarningModal } from "../../../../components/WarningModal";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { WhatsappMetaAIAgentDto } from "../../../../model/integration/dto/meta/WhatsappMetaAIAgentDto";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { TestIDs } from "../../../../tests/types/testIds";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { MenuProps, Switch, Tooltip } from "../../../../utils/local-antd";
import { DrawerDefaultsExtra } from "../../../tasks/components/DrawerDefaultsExtra";
import {
  AI_PROVIDER_MODELS,
  FaqPair,
  formatFaqPairs,
  providerLabelByModel,
} from "../../../settings/components/chatbot/chatbot.utils";

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xl};
  width: 100%;
`;

const ModelButton = styled(Button)<{ $valid: boolean }>`
  width: fit-content;
  padding: ${Spacing.s};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.main};
  text-align: start;
  border: 1px solid
    ${({ theme, $valid }) => ($valid ? theme.main : theme.danger)};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.white};
  }
`;

const ToggleCard = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  border: 1px solid ${({ theme }) => theme.sub};
  border-radius: ${Rounded.md};
  background-color: ${({ theme }) => theme.lightSub};
`;

const RemoveButton = styled(Button)`
  width: fit-content;
  margin: 0;
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.danger};
  border: 1px solid ${({ theme }) => theme.danger};

  &:hover {
    background-color: ${({ theme }) => theme.danger};
    color: ${({ theme }) => theme.white};
  }
`;

const StyledInfo = styled(Tooltip)`
  cursor: help;
`;

const ModelLabel = styled(Text)<{ $index: number }>`
  display: inline-block;
  color: ${({ theme }) => theme.white};
  padding: ${Spacing.xs};
  border-radius: ${Rounded.md};
  background-color: ${({ $index, theme }) =>
    $index === 0 ? theme.danger : $index === 1 ? theme.warning : theme.success};
  width: fit-content;
`;

type FormValues = WhatsappMetaAIAgentDto & { aiFaqPairs: FaqPair[] };

type Props = {
  open: boolean;
  onClose: () => void;
};

export const WAAIAgentDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const agent = integration?.config?.aiAgent;

  const { control, handleSubmit, reset, watch } = useForm<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "aiFaqPairs",
  });

  const enabled = watch("enabled");

  const modelOptions = (
    onSelectModel: (model: string) => void,
  ): MenuProps["items"] =>
    AI_PROVIDER_MODELS.map((provider) => ({
      key: `provider-${provider.providerKey}`,
      label: t(provider.providerLabel),
      "data-testid":
        TestIDs.integration.whatsappMeta.aiAgentDrawer.model.provider(
          provider.providerKey,
        ),
      children: provider.models.map((m, i) => ({
        key: `model-${provider.providerKey}-${m.value}`,
        "data-testid":
          TestIDs.integration.whatsappMeta.aiAgentDrawer.model.option(m.value),
        label: m.label ? (
          <Trans
            i18nKey={m.label}
            components={[<ModelLabel $index={i} fontSize="xs" />]}
          >
            {m.value}
          </Trans>
        ) : (
          m.value
        ),
        onClick: () => onSelectModel(m.value),
      })),
    }));

  const onSubmit = async (values: FormValues) => {
    try {
      setLoading(true);

      await dispatch(
        integrationActions.updateWhatsappMetaAiAgent({
          userId,
          integrationId: integration!._id,
          enabled: Boolean(values.enabled),
          name: values.name?.trim(),
          aiKey: values.aiKey?.trim(),
          aiModel: values.aiModel,
          aiProvider: providerLabelByModel.get(values.aiModel) || "openai",
          aiPromptTemplate: values.aiPromptTemplate?.trim(),
          aiFAQs: formatFaqPairs(values.aiFaqPairs),
          aiCrawledDataSources: (values.aiCrawledDataSources || []).filter(
            Boolean,
          ),
        }),
      ).unwrap();

      await dispatch(integrationActions.getUserIntegrations({ userId }));

      if (integration?.config?.phoneNumberId) {
        await dispatch(
          integrationActions.getWhatsAppMetaChatsList({
            userId,
            integrationId: integration._id,
            phoneNumberId: integration.config.phoneNumberId,
          }),
        );
      }

      AtiraToast.success(t("integration.whatsapp.meta.aiAgent.saveSuccess"));
      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reset({
      userId,
      integrationId: integration?._id || "",
      enabled: agent?.enabled ?? false,
      name: agent?.name || "",
      // The server only ever hands us the masked key, an untouched field means
      // "keep the stored one"
      aiKey: agent?.aiKeyShort || "",
      aiModel: agent?.aiModel || "",
      aiPromptTemplate: agent?.aiPromptTemplate || "",
      aiCrawledDataSources: agent?.aiCrawledDataSources?.length
        ? agent.aiCrawledDataSources
        : [""],
      aiFaqPairs: agent?.aiFAQs?.length
        ? agent.aiFAQs.map(([question, answer]) => ({ question, answer }))
        : [{ question: "", answer: "" }],
    });
  }, [agent, integration?._id, userId, reset, open]);

  const onRemove = async () => {
    try {
      setRemoveLoading(true);

      await dispatch(
        integrationActions.deleteWhatsappMetaAIAgent({
          userId,
          integrationId: integration!._id,
        }),
      ).unwrap();

      await dispatch(integrationActions.getUserIntegrations({ userId }));

      AtiraToast.success(t("integration.whatsapp.meta.aiAgent.removeSuccess"));
      setRemoveModalOpen(false);
      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setRemoveLoading(false);
    }
  };

  if (!integration) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="large"
      title={t("integration.whatsapp.meta.aiAgent.title")}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSubmit)}
          onCancel={onClose}
          config={{ loading }}
          submitTitle={t("common.save")}
          submitTestId={TestIDs.integration.whatsappMeta.aiAgentDrawer.submit}
        />
      }
    >
      <Container>
        <Flex alignItems="center" gap="s">
          <AtiraIcon icon={faRobot} color="main" size="xl" />
          <Text fontSize="l" fontWeight="bold" color="main">
            {t("integration.whatsapp.meta.aiAgent.title")}
          </Text>
        </Flex>

        <Info info={t("integration.whatsapp.meta.aiAgent.info")} />

        <Controller
          name="enabled"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ToggleCard>
              <Flex alignItems="center" gap="s">
                <Switch
                  onChange={onChange}
                  checked={Boolean(value)}
                  className={
                    TestIDs.integration.whatsappMeta.aiAgentDrawer.fields
                      .enabled
                  }
                />
                <Text fontWeight="bold">
                  {t("integration.whatsapp.meta.aiAgent.enabled.title")}
                </Text>
              </Flex>
              <Info
                info={t("integration.whatsapp.meta.aiAgent.enabled.info")}
              />
            </ToggleCard>
          )}
        />

        <Controller
          name="name"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Input
              value={value || ""}
              onChange={onChange}
              title={t("integration.whatsapp.meta.aiAgent.name.title")}
              placeholder={t(
                "integration.whatsapp.meta.aiAgent.name.placeholder",
              )}
              info={t("integration.whatsapp.meta.aiAgent.name.info")}
              maxLength={Lengths.DESCRIPTION}
              dataTestId={
                TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.name
              }
              valid={!error?.message}
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          name="aiModel"
          control={control}
          rules={{
            required: {
              value: Boolean(enabled),
              message: t("common.this_field_required"),
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const providerLabel = value
              ? providerLabelByModel.get(value)
              : null;

            return (
              <Flex flexDirection="column" gap="s">
                <Text>{t("settings.aiChatbot.model.modelTitle")}</Text>

                <AtiraDropdown
                  items={modelOptions(onChange)}
                  placement="bottomLeft"
                  trigger={["click"]}
                  arrow={{ pointAtCenter: true }}
                  destroyOnHidden
                >
                  <ModelButton
                    hover
                    icon={faArrowAltCircleDown}
                    $valid={!error?.message}
                    className={
                      TestIDs.integration.whatsappMeta.aiAgentDrawer.model.open
                    }
                  >
                    {value
                      ? providerLabel
                        ? `${providerLabel} / ${value}`
                        : value
                      : t("common.select")}
                  </ModelButton>
                </AtiraDropdown>

                {error?.message ? (
                  <Text color="danger" fontSize="s">
                    {error.message}
                  </Text>
                ) : null}
              </Flex>
            );
          }}
        />

        <Controller
          name="aiKey"
          control={control}
          rules={{
            required: {
              value: Boolean(enabled),
              message: t("common.this_field_required"),
            },
          }}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Textarea
              value={value || ""}
              onChange={onChange}
              title={t("settings.aiChatbot.key.inputTitle")}
              placeholder={t("settings.aiChatbot.key.inputPlaceholder")}
              info={t("integration.whatsapp.meta.aiAgent.key.info")}
              valid={!error?.message}
              errorMessage={error?.message}
              required
              dataTestId={
                TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.apiKey
              }
            />
          )}
        />

        <Controller
          name="aiPromptTemplate"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Textarea
              value={value || ""}
              onChange={onChange}
              title={t("settings.aiChatbot.prompt.promptTitle")}
              placeholder={t("settings.aiChatbot.prompt.promptPlaceholder")}
              info={t("integration.whatsapp.meta.aiAgent.context.info")}
              valid={!error?.message}
              errorMessage={error?.message}
              maxLength={Lengths.LONG}
              rows={6}
              dataTestId={
                TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.prompt
              }
            />
          )}
        />

        <Controller
          name="aiCrawledDataSources.0"
          control={control}
          render={({ fieldState: { error }, field: { onChange, value } }) => (
            <Input
              value={value || ""}
              onChange={onChange}
              title={t(
                "settings.aiChatbot.crawledDataSources.crawledDataSourcesTitle",
              )}
              placeholder={t(
                "settings.aiChatbot.crawledDataSources.placeholder",
              )}
              info={t(
                "settings.aiChatbot.crawledDataSources.crawledDataSourcesInfo",
              )}
              valid={!error?.message}
              errorMessage={error?.message}
              maxLength={Lengths.LONG}
              dataTestId={
                TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.dataSource
              }
            />
          )}
        />

        <Flex flexDirection="column" gap="m">
          <Flex alignItems="center" gap="s">
            <StyledInfo title={t("settings.aiChatbot.faq.title.info")}>
              <span>
                <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
              </span>
            </StyledInfo>
            <Text>{t("settings.aiChatbot.faq.title")}</Text>
            <Text fontSize="s" color="gray">
              ({t("common.optional")})
            </Text>
          </Flex>

          <Flex flexDirection="column" gap="l">
            {fields.map((field, index) => (
              <Flex key={field.id} gap="m" alignItems="start">
                <Flex flexDirection="column" gap="s" width="100%">
                  <Controller
                    control={control}
                    name={`aiFaqPairs.${index}.question`}
                    render={({ field: { value, onChange } }) => (
                      <Input
                        value={value || ""}
                        onChange={onChange}
                        placeholder={t(
                          "settings.aiChatbot.faq.question.input.title",
                          { index: index + 1 },
                        )}
                        dataTestId={TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.faqQuestion(
                          index,
                        )}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`aiFaqPairs.${index}.answer`}
                    render={({ field: { value, onChange } }) => (
                      <Textarea
                        value={value || ""}
                        onChange={onChange}
                        placeholder={t(
                          "settings.aiChatbot.faq.question.input.placeholder",
                          { index: index + 1 },
                        )}
                        dataTestId={TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.faqAnswer(
                          index,
                        )}
                      />
                    )}
                  />
                </Flex>

                <Clickable
                  onClick={() => remove(index)}
                  dataTestId={TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.faqButtonRemove(
                    index,
                  )}
                >
                  <Tooltip title={t("common.delete")}>
                    <span />
                    <AtiraIcon icon={faTrash} color="main" />
                  </Tooltip>
                </Clickable>
              </Flex>
            ))}
          </Flex>

          <Button
            icon={faPlus}
            margin="none"
            width="18rem"
            onClick={() => append({ question: "", answer: "" })}
            dataTestId={
              TestIDs.integration.whatsappMeta.aiAgentDrawer.fields.faqButtonAdd
            }
          >
            {t("settings.aiChatbot.faq.addButton")}
          </Button>
        </Flex>

        {agent ? (
          <RemoveButton
            icon={faTrash}
            onClick={() => setRemoveModalOpen(true)}
            dataTestId={
              TestIDs.integration.whatsappMeta.aiAgentDrawer.remove.button
            }
          >
            {t("integration.whatsapp.meta.aiAgent.remove.button")}
          </RemoveButton>
        ) : null}
      </Container>

      <WarningModal
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        onConfirm={onRemove}
        loading={removeLoading}
        title={t("integration.whatsapp.meta.aiAgent.remove.modal.title")}
        description={t(
          "integration.whatsapp.meta.aiAgent.remove.modal.description",
        )}
        confirmButtonDataTestId={
          TestIDs.integration.whatsappMeta.aiAgentDrawer.remove.confirm
        }
      />
    </Drawer>
  );
};
